import React, { useMemo } from "react";
import { AlertCircle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import type { BaseTypeSelectProps, SourceType } from "@/types/silae";
import { useTranslation } from "react-i18next";

/**
 * Composant réutilisable pour la sélection du type de base SILAE
 * Optimisé pour la nouvelle structure hiérarchique
 */
export const BaseTypeSelect: React.FC<BaseTypeSelectProps> = ({
  value,
  onValueChange,
  error,
  touched,
  disableComplexExpression = false,
}) => {
  const { t } = useTranslation();

  const sourceTypes = useMemo(
    () =>
      [
        { value: "fixed" as SourceType, hasBadge: false },
        { value: "evp" as SourceType, hasBadge: false },
        { value: "salary_base" as SourceType, hasBadge: true },
        { value: "salary_gross" as SourceType, hasBadge: true },
        { value: "salary_net" as SourceType, hasBadge: true },
        { value: "stored_variable" as SourceType, hasBadge: true },
        { value: "cumul_bulletins" as SourceType, hasBadge: true },
        { value: "cumul_stock_var" as SourceType, hasBadge: true },
        { value: "advanced_variable" as SourceType, hasBadge: true },
        { value: "complex_expression" as SourceType, hasBadge: true },
        { value: "questionnaire_variable" as SourceType, hasBadge: true },
        { value: "manual_formula" as SourceType, hasBadge: true },
      ]
        .filter(
          (item) =>
            !(disableComplexExpression && item.value === "complex_expression"),
        )
        .map((item) => ({
          ...item,
          label: t(`baseTypeSelect.options.${item.value}`),
          badge: item.hasBadge
            ? t(`baseTypeSelect.badges.${item.value}`)
            : undefined,
        })),
    [t, disableComplexExpression],
  );

  return (
    <div className="space-y-2">
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger className={touched && error ? "border-red-500" : ""}>
          <SelectValue placeholder={t("baseTypeSelect.placeholder")} />
        </SelectTrigger>
        <SelectContent>
          {sourceTypes.map((sourceType) => (
            <SelectItem key={sourceType.value} value={sourceType.value}>
              <div className="flex items-center justify-between w-full">
                <span>{sourceType.label}</span>
                {sourceType.badge && (
                  <Badge variant="outline" className="ml-2">
                    {sourceType.badge}
                  </Badge>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {touched && error && (
        <p className="text-sm text-red-500 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {error}
        </p>
      )}
    </div>
  );
};
